import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getProjects } from "@/lib/refData";
import { toast } from "sonner";
import { Mic, Square, Loader2, CheckCircle2, AlertTriangle, ArrowRight,
         Undo2, Pencil, MessageSquare } from "lucide-react";
import { SectionBody } from "@/components/Update360";
import { saveDraft, loadDraft, clearDraft } from "@/lib/drafts";
import EmptyTip from "@/components/EmptyTip";
import { subscribePush, pushSupported } from "@/lib/push";
import CustomerUpdateSheet from "@/components/CustomerUpdateSheet";

const SECTION_KEYS = ["daily_log", "timecards", "equipment", "receipt", "status", "tasks", "schedule", "change_order", "customer_message", "checklist", "photos"];

function pickMime() {
  const MR = window.MediaRecorder;
  if (!MR) return null;
  for (const t of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) {
    if (MR.isTypeSupported?.(t)) return t;
  }
  return null;
}

// 360 Update, voice-first, inside the unified Assistant panel (Phase 4 merge).
// capture → transcript → review drafts → "Approve all". Never saves before approval.
// initialText/initialDraft: typed text routed here from the assistant chat box —
// skips capture and goes straight to processing/review.
export default function AssistantUpdate({ projectId, projectName, onBack, onSaved, initialText = "", initialDraft = null, onClosePanel = null, onScheduleCommand = null }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("capture"); // capture | transcript | processing | review | done
  const [listening, setListening] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState("");
  const draftKey = `bf.draft.360.${projectId || "general"}`; // Round 13: unsaved-update autosave
  const [pendingDraft, setPendingDraft] = useState(null);
  useEffect(() => {
    const d = loadDraft(draftKey);
    if (d && !transcript) setPendingDraft(d);
  }, [draftKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (phase === "done") clearDraft(draftKey); }, [phase, draftKey]);
  // Group B5: warm the interpret pipeline while the user starts talking/typing,
  // so the first real call isn't the 70s cold one.
  useEffect(() => {
    try { if (sessionStorage.getItem("bf.360warmed")) return; sessionStorage.setItem("bf.360warmed", "1"); } catch { /* ignore */ }
    api.get("/update360/warm").catch(() => {});
  }, []);
  const [draft, setDraft] = useState(null);        // interpret response
  const [include, setInclude] = useState({});      // section -> bool
  const [editKey, setEditKey] = useState(null);
  const [editText, setEditText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [undoLeft, setUndoLeft] = useState(0);
  const [team, setTeam] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [pickedCrew, setPickedCrew] = useState({}); // memberId -> hours
  const [extraWorkers, setExtraWorkers] = useState([]); // name-only workers (no account)
  const [timecardPicks, setTimecardPicks] = useState({}); // entryIdx -> {user_id, name} for unmatched workers
  const [equipPicks, setEquipPicks] = useState({});   // itemIdx -> {equipment_id, equipment_name, equipment_kind}
  const [eqOptions, setEqOptions] = useState([]);
  const [receiptEdits, setReceiptEdits] = useState(null);
  const [stageIdx, setStageIdx] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const blobRef = useRef(null); // keep recording for retry after STT failure
  const lastApproveRef = useRef(null); // last approve payload — Retry reuses it for failed sections
  const [showGuide, setShowGuide] = useState(false);
  const [guideTopics, setGuideTopics] = useState([]);
  const [keptBlob, setKeptBlob] = useState(null); // interrupted recording (phone call etc.) — kept, sendable
  // Short one-paragraph-per-feature guide, served from the shared How-To KB.
  const toggleGuide = async () => {
    if (showGuide) { setShowGuide(false); return; }
    if (!guideTopics.length) {
      try { const { data } = await api.get("/help/topics"); setGuideTopics(data.topics || []); } catch (e) { /* keep list empty */ }
    }
    setShowGuide(true);
  };

  // Flow C: after a successful approve, offer the one-tap customer update.
  const [cuPrev, setCuPrev] = useState(null);
  const [cuOpen, setCuOpen] = useState(false);
  useEffect(() => {
    if (phase === "done" && result && !result.undone && draft?.project_id) {
      setCuPrev(null);
      api.get(`/update360/customer-update/${draft.project_id}/preview`)
        .then(({ data }) => setCuPrev(data)).catch(() => {});
    }
  }, [phase, result, draft?.project_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { clearInterval(timerRef.current); try { recRef.current?.stop(); } catch (e) {} }, []);

  // Anonymous labor ("2 guys 3 hours") → reviewer picks real crew. Shared by the
  // voice path (process) AND the typed path (initialDraft) — the typed path used
  // to skip this, leaving an empty picker that blocked Approve all (Round 9-C).
  const setupCrewPick = async (data) => {
    const tc = (data.sections || {}).timecards;
    if (!tc?.needs_crew_pick) return;
    setTeamLoading(true);
    try {
    // /members is role-safe (the old /crew call 403'd for non-admins → empty picker)
    const { data: md } = await api.get("/members").catch(() => ({ data: { items: [] } }));
    const projs = await getProjects().catch(() => []);
    const proj = (projs || []).find((p) => p.id === data.project_id);
    const crewIds = new Set(proj?.crew_ids || []);
    const members = md.items || [];
    // Project crew first, then everyone else — tap-to-select in that order.
    members.sort((a, b) => (crewIds.has(b.id) ? 1 : 0) - (crewIds.has(a.id) ? 1 : 0) || (a.name || "").localeCompare(b.name || ""));
    setTeam(members);
    const pre = {};
    (proj?.crew_ids || []).forEach((id) => { if (members.some((m) => m.id === id)) pre[id] = tc.anon_hours; });
    if (Object.keys(pre).length === 0 && members.length) {
      // Project has no assigned crew — pre-select the first N team members
      members.slice(0, tc.anon_count || 1).forEach((m) => { pre[m.id] = tc.anon_hours; });
    }
    setPickedCrew(pre);
    // Fewer accounts than people mentioned → name-only rows for the rest.
    const shortage = Math.max(0, (tc.anon_count || 0) - (Object.keys(pre).length + (tc.entries || []).length));
    if (shortage > 0) setExtraWorkers(Array.from({ length: shortage }, () => ({ name: "", hours: tc.anon_hours })));
    } finally { setTeamLoading(false); }
  };

  // Typed entry from the assistant chat box: jump straight into the 360 pipeline.
  useEffect(() => {
    if (initialDraft) {
      setDraft(initialDraft);
      const inc = {};
      Object.keys(initialDraft.sections || {}).forEach((k) => { inc[k] = true; });
      setInclude(inc);
      setPhase("review");
      setupCrewPick(initialDraft);
    } else if (initialText.trim()) {
      setTranscript(initialText);
      process(initialText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data?.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setListening(false);
        clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        if (blob.size < 500) return;
        blobRef.current = blob;
        await transcribe(blob);
      };
      recRef.current = rec;
      rec.start();
      setListening(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      toast.error(t("u360_mic_blocked_type"));
      setPhase("transcript"); // mic blocked → open the text box instead
    }
  };

  const stopRec = () => { try { recRef.current?.stop(); } catch (e) {} };

  // Phone call / app switch mid-recording: stop gracefully. MediaRecorder keeps
  // every chunk captured up to that point, so the partial recording stays
  // sendable (blobRef) instead of being lost with the interruption.
  useEffect(() => {
    if (!listening) return undefined;
    const onVis = () => { if (document.visibilityState === "hidden") stopRec(); };
    const onPageHide = () => stopRec();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [listening]);

  const transcribe = async (blob) => {
    setPhase("processing");
    const ext = (blob.type || "").includes("mp4") ? "m4a" : "webm";
    const fd = new FormData();
    fd.append("lang", i18n.language?.startsWith("es") ? "es" : "en");
    fd.append("file", new File([blob], `update.${ext}`, { type: blob.type }));
    try {
      const { data } = await api.post("/stt", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      setTranscript(data.text || "");
      process(data.text || ""); // straight to drafting — the review screen already supports editing
    } catch (e) {
      toast.error(t("u360_stt_failed"));
      setKeptBlob(blob); // recording kept — capture screen offers "Send recording"
      setPhase("capture");
    }
  };

  const process = async (textOverride) => {
    const text = String(textOverride ?? transcript).trim();
    if (!text || busy) return;
    setBusy(true);
    setPhase("processing");
    setStageIdx(0);
    try {
      const { data } = await api.post("/update360/interpret", {
        transcript: text,
        lang: i18n.language?.startsWith("es") ? "es" : "en",
        local_date: new Date().toLocaleDateString("en-CA"),
        context_project_id: projectId || null,
      }, { timeout: 90000 });
      // Schedule commands typed here re-route to the chat flow's preview card.
      if (data?.schedule_command) {
        onScheduleCommand?.(text);
        return;
      }
      // Group B3: never clobber a good draft with an empty/garbage interpret.
      if (!data?.choices && !Object.keys(data?.sections || {}).length) {
        toast.error(t("bg_error"));
        setPhase(draft ? "review" : "capture");
        return;
      }
      setDraft(data);
      setKeptBlob(null); // draft succeeded — no kept recording to offer
      const inc = {};
      Object.keys(data.sections || {}).forEach((k) => { inc[k] = true; });
      setInclude(inc);
      setPhase("review");
      // Job Costs: unresolved equipment hints need a reviewer pick; the receipt draft is editable.
      const eqSec = (data.sections || {}).equipment;
      if (eqSec?.items?.some((it) => !it.equipment_id)) {
        api.get("/jobcosts/equipment").then(({ data: eqd }) => setEqOptions((eqd.items || []).filter((e) => e.active))).catch(() => {});
      }
      const rcSec = (data.sections || {}).receipt;
      if (rcSec) {
        setReceiptEdits({ vendor: rcSec.vendor || "", total: rcSec.total != null ? String(rcSec.total) : "",
                          receipt_date: rcSec.receipt_date || "", category: rcSec.category || "materials" });
      }
      await setupCrewPick(data);
    } catch (e) {
      toast.error(t("bg_error"));
      setPhase("capture"); // transcript preserved in the box — Continue retries
    } finally { setBusy(false); }
  };

  const chooseProject = async (pid) => {
    try {
      const { data } = await api.post(`/update360/${draft.id}/choose-project`, { project_id: pid });
      setDraft((d) => ({ ...d, project_id: data.project_id, project_name: data.project_name, choices: null }));
    } catch (e) { toast.error(t("bg_error")); }
  };

  const approveAll = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const edits = {};
      if (editKey && editText) edits[editKey] = { text: editText };
      if (draft.sections?.timecards?.needs_crew_pick && !(draft.sections.timecards.entries || []).length) {
        // Anonymous labor: reviewer-picked crew + name-only workers become the entries.
        const entries = Object.entries(pickedCrew)
          .map(([id, hours]) => ({ user_id: id, name: (team.find((x) => x.id === id) || {}).name || id, hours }))
          .filter((e) => e.hours > 0);
        for (const w of extraWorkers) {
          if (w.name.trim() && w.hours > 0) entries.push({ name: w.name.trim(), hours: w.hours, no_account: true });
        }
        if (entries.length) {
          edits.timecards = { entries };
        } else {
          // Never block Approve all on timecards — save everything else, skip hours honestly.
          include.timecards = false;
          edits.timecards = { entries: [] };
          toast(t("u360_tc_skipped"), { icon: "⏭" });
        }
      } else if (draft.sections?.timecards?.entries?.length) {
        // Named workers: attach the reviewer's picks for any unmatched names.
        edits.timecards = { entries: draft.sections.timecards.entries
          .map((e, i) => ({ ...e, ...(timecardPicks[i] || {}) })) };
      }
      if (draft.sections?.equipment) {
        edits.equipment = { items: (draft.sections.equipment.items || []).map((it, i) => ({ ...it, ...(equipPicks[i] || {}) })) };
      }
      if (draft.sections?.receipt && receiptEdits) {
        edits.receipt = { vendor: receiptEdits.vendor,
                          total: receiptEdits.total === "" ? null : parseFloat(receiptEdits.total),
                          receipt_date: receiptEdits.receipt_date, category: receiptEdits.category };
      }
      lastApproveRef.current = { include: { ...include }, edits };
      const { data } = await api.post(`/update360/${draft.id}/approve`,
        { include, edits, method: "tap" }, { timeout: 90000 });
      setResult(data);
      // First successful Approve is the right moment to ask for push (iOS needs
      // the installed app; we never ask at signup). Asked once per device.
      try {
        if (!localStorage.getItem("bf.pushAsked") && pushSupported() && Notification.permission === "default") {
          localStorage.setItem("bf.pushAsked", "1");
          subscribePush().catch(() => {});
        }
      } catch (e) { /* non-browser */ }
      setPhase("done");
      // Rewrite the chat's "opened review" placeholder with the saved summary (R10-6).
      const ok = (data.results || []).filter((r) => !r.skipped && !r.failed);
      if (ok.length) onSaved?.(t("u360_chat_saved", { list: ok.map((r) => (r.detail ? `${r.label} ${r.detail}` : r.label)).join(", ") }));
      if (data.undo_until) {
        const left = Math.max(0, Math.floor((new Date(data.undo_until) - Date.now()) / 1000));
        setUndoLeft(left);
      }
    } catch (e) {
      const d = String(e.response?.data?.detail || "");
      toast.error(d.startsWith("perm_denied") ? t("bgv_perm_denied") : t("u360_approve_failed"));
    } finally { setBusy(false); }
  };

  // Partial approve: re-run ONLY the failed sections (backend dedupes the rest).
  const retryFailed = async () => {
    if (busy || !result?.failed?.length) return;
    setBusy(true);
    try {
      const prev = lastApproveRef.current || { include: {}, edits: {} };
      const inc = {};
      const edits = {};
      result.failed.forEach((k) => { inc[k] = true; if (prev.edits?.[k]) edits[k] = prev.edits[k]; });
      const { data } = await api.post(`/update360/${draft.id}/approve`,
        { include: inc, edits, method: "tap" }, { timeout: 90000 });
      setResult(data);
      if (!data.failed?.length) toast.success(t("u360_saved_prefix"));
    } catch (e) {
      const d = String(e.response?.data?.detail || "");
      toast.error(d.startsWith("perm_denied") ? t("bgv_perm_denied") : t("u360_approve_failed"));
    } finally { setBusy(false); }
  };

  const undo = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/update360/${draft.id}/undo`);
      toast.success(t("u360_undone"));
      onBack();
    } catch (e) { toast.error(t("u360_undo_failed")); }
    setBusy(false);
  };

  useEffect(() => {
    if (undoLeft <= 0) return;
    const iv = setInterval(() => setUndoLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(iv);
  }, [undoLeft]);

  const PROCESS_STAGES = ["u360_stage_read", "u360_stage_log", "u360_stage_sched", "u360_stage_crew"];
  useEffect(() => {
    if (phase !== "processing") return;
    setStageIdx(0);
    const iv = setInterval(() => setStageIdx((s) => (s + 1) % PROCESS_STAGES.length), 2600);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="assistant-update">
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2">
        <button onClick={onBack} data-testid="u360-back-chat"
          className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-slate-300 px-3 text-[11px] font-bold text-slate-600 active:scale-95">
          <MessageSquare className="h-3.5 w-3.5" /> {t("ap_back_chat")}
        </button>
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">360 Assistant</span>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {/* ---------- capture (voice first) ---------- */}
        {phase === "capture" && (
          <div className="space-y-4" data-testid="u360-capture">
            {projectName && (
              <div className="rounded-xl bg-brand/5 border border-brand/20 px-3 py-2 text-xs font-bold text-branddeep" data-testid="u360-context">
                {t("ap_helping", { name: projectName })}
              </div>
            )}
            <div className="flex flex-col items-center gap-3 py-4">
              <button onClick={listening ? stopRec : startRec} data-testid="u360-mic"
                aria-label={listening ? t("ap_stop") : t("u360_hold_to_talk")}
                className={`flex h-20 w-20 items-center justify-center rounded-full text-white shadow-xl active:scale-90 transition-transform ${listening ? "bg-red-500 animate-pulse" : "bg-brand"}`}>
                {listening ? <Square className="h-7 w-7" /> : <Mic className="h-8 w-8" />}
              </button>
              {listening && (
                <div className="flex items-center gap-2 text-sm font-bold text-red-600" data-testid="u360-recording">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" /> {mm}:{ss}
                </div>
              )}
              <p className="text-xs text-slate-500 text-center max-w-xs">
                {blobRef.current ? t("u360_retry_stt") : t("u360_tap_to_talk")}
              </p>
              {blobRef.current && (
                <button onClick={() => transcribe(blobRef.current)} data-testid="u360-retry-stt"
                  className="text-xs font-bold text-brand underline">{t("u360_retry_stt_btn")}</button>
              )}
            </div>
            {pendingDraft && !transcript && (
              <div className="mb-3 rounded-xl border border-brand/30 bg-brand/5 p-3" data-testid="u360-draft-banner">
                <p className="text-xs font-semibold text-slate-700">{t("draft_restore_q", { time: new Date(pendingDraft.ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) })}</p>
                <div className="mt-2 flex gap-2">
                  <button onClick={() => { setTranscript(pendingDraft.text); setPendingDraft(null); }} data-testid="u360-draft-restore"
                    className="min-h-[40px] flex-1 rounded-full bg-brand px-4 text-xs font-bold text-white active:scale-95">{t("draft_restore")}</button>
                  <button onClick={() => { clearDraft(draftKey); setPendingDraft(null); }} data-testid="u360-draft-discard"
                    className="min-h-[40px] flex-1 rounded-full border border-slate-300 px-4 text-xs font-bold text-slate-600 active:scale-95">{t("discard_go")}</button>
                </div>
              </div>
            )}
            <div className="border-t border-slate-100 pt-3">
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-1.5">{t("u360_type_instead")}</label>
              <textarea rows={3} value={transcript} onChange={(e) => { setTranscript(e.target.value); if (e.target.value.trim()) saveDraft(draftKey, e.target.value); else clearDraft(draftKey); }}
                data-testid="u360-transcript-input" placeholder={t("u360_placeholder")}
                className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900" />
            </div>
            {keptBlob && (
              <div className="flex items-center justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2" data-testid="u360-kept-recording">
                <span className="text-xs font-semibold text-amber-800">{t("u360_rec_kept")}</span>
                <button onClick={() => { const b = keptBlob; setKeptBlob(null); transcribe(b); }} data-testid="u360-send-kept"
                  className="inline-flex min-h-[36px] items-center rounded-full bg-brand px-4 text-xs font-bold text-white active:scale-95">
                  {t("u360_send_recording")}
                </button>
              </div>
            )}
            <div>
              <button type="button" onClick={toggleGuide} data-testid="u360-guide-toggle"
                className="text-xs font-bold text-brand underline underline-offset-2">{t("u360_guide_toggle")}</button>
              {showGuide && (
                <ul className="mt-2 max-h-56 space-y-2 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="u360-guide">
                  {guideTopics.map((g) => (
                    <li key={g.id}>
                      <span className="block text-xs font-bold text-slate-800">{g.title}</span>
                      <span className="block text-[11px] leading-snug text-slate-500">{g.body}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button type="button" onClick={() => {
              // Close the 360 panel FIRST (typed text survives via the autosave
              // draft), then open the camera on top in Walkthrough mode. The
              // delay lets the panel's history entry settle before the camera
              // pushes its own (modalBack stack ordering).
              onClosePanel?.();
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent("bf:open-camera", { detail: { mode: "walkthrough", projectId: projectId || null, projectName: projectName || "" } }));
              }, 120);
            }} data-testid="u360-start-walkthrough"
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-brand/40 bg-brand/5 px-3 py-2.5 text-xs font-bold text-branddeep active:scale-[0.99]">
              <Mic className="h-3.5 w-3.5" /> {t("wt_start")}
            </button>
            <EmptyTip slot="u360" />
            <div className="flex gap-2">
              <button onClick={() => process()} disabled={!transcript.trim() || busy}
                data-testid="u360-to-review"
                className="flex-1 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-brand text-sm font-bold text-white active:scale-95 disabled:opacity-50">
                {t("u360_continue")} <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={onBack} data-testid="u360-cancel"
                className="min-h-[48px] rounded-full border border-slate-300 px-5 text-sm font-bold text-slate-600 active:scale-95">
                {t("action_cancel")}
              </button>
            </div>
          </div>
        )}

        {/* ---------- transcript edit ---------- */}
        {phase === "transcript" && (
          <div className="space-y-4" data-testid="u360-transcript">
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-400">{t("u360_edit_transcript")}</label>
            <textarea rows={6} value={transcript} onChange={(e) => setTranscript(e.target.value)}
              data-testid="u360-transcript-edit"
              className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900" />
            <div className="flex gap-2">
              <button onClick={() => process()} disabled={busy || !transcript.trim()} data-testid="u360-process"
                className="flex-1 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-brand text-sm font-bold text-white active:scale-95 disabled:opacity-50">
                {t("u360_process")} <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => setPhase("capture")} data-testid="u360-back"
                className="min-h-[48px] rounded-full border border-slate-300 px-5 text-sm font-bold text-slate-600 active:scale-95">
                {t("action_back", "Back")}
              </button>
            </div>
          </div>
        )}

        {/* ---------- processing ---------- */}
        {phase === "processing" && (
          <div className="flex flex-col gap-3 py-10" data-testid="u360-processing">
            <div className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-brand" />
              <p className="text-sm font-bold text-slate-700">{t("u360_processing_title")}</p>
            </div>
            <ol className="space-y-2.5 pl-1">
              {PROCESS_STAGES.map((k, i) => (
                <li key={k} className="flex items-center gap-2.5" data-testid={`u360-step-${i}`}>
                  {i < stageIdx
                    ? <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                    : i === stageIdx
                      ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand" />
                      : <span className="h-4 w-4 shrink-0 rounded-full border-2 border-slate-200" />}
                  <span className={`text-xs font-semibold ${i <= stageIdx ? "text-slate-800" : "text-slate-400"}`}
                    data-testid={i === stageIdx ? "u360-processing-stage" : undefined}>{t(k)}</span>
                </li>
              ))}
            </ol>
            <p className="text-xs text-slate-400">{t("u360_processing_hint")}</p>
          </div>
        )}

        {/* ---------- review drafts → Approve all ---------- */}
        {phase === "review" && draft && (
          <div className="space-y-4" data-testid="u360-review">
            <h3 className="font-heading text-lg font-extrabold text-slate-900">{t("u360_review_title")}</h3>
            {draft.choices?.length > 0 && (
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 space-y-1.5" data-testid="u360-choices">
                <p className="text-xs font-bold text-amber-800">{t("u360_which_project")}</p>
                {draft.choices.map((c) => (
                  <button key={c.id} onClick={() => chooseProject(c.id)} data-testid={`u360-choice-${c.id}`}
                    className="flex w-full items-center justify-between rounded-xl border border-amber-300 bg-white px-3.5 py-2.5 text-left min-h-[44px] active:scale-[0.99]">
                    <span className="text-sm font-bold text-slate-900">{c.label}</span>
                    <span className="text-xs text-slate-500 truncate ml-2">{c.sub}</span>
                  </button>
                ))}
              </div>
            )}
            {draft.project_name && (
              <div className="rounded-xl bg-brand/5 border border-brand/20 px-3 py-2 text-xs font-bold text-branddeep" data-testid="u360-project">
                {t("ap_helping", { name: draft.project_name })}
              </div>
            )}
            {(draft.lang || "").startsWith("es") && draft.transcript && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2" data-testid="u360-original">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t("u360_original_label")}</span>
                <p className="mt-0.5 text-xs italic text-slate-600">{draft.transcript}</p>
              </div>
            )}
            {draft.extra_work_hits?.length > 0 && (
              <div className="rounded-xl border-2 border-orange-300 bg-orange-50 p-3" data-testid="u360-extra-work">
                <div className="flex items-center gap-2 text-xs font-bold text-orange-800">
                  <AlertTriangle className="h-4 w-4 shrink-0" /> {t("u360_extra_work_detected")}
                </div>
                <p className="mt-1 text-[11px] text-orange-700">{t("u360_extra_work_hint")}</p>
              </div>
            )}
            {SECTION_KEYS.map((k) => {
              const s = (draft.sections || {})[k];
              const isPhotoSection = k === "photos";
              if (!s && !isPhotoSection) return null;
              const warn = (k === "change_order" && !s?.amount) || (k === "customer_message" && !s?.text) ||
                (k === "schedule" && !(s?.entries || []).some((e) => e?.title && e?.date)) ||
                (k === "equipment" && (s?.items || []).some((it, i) => (!it.equipment_id && !equipPicks[i]?.equipment_id) || it.hours == null)) ||
                (k === "receipt" && (receiptEdits ? receiptEdits.total === "" : s?.total == null));
              return (
                <div key={k} className={`rounded-xl border-2 p-3.5 ${include[k] ? "border-brand/30 bg-white" : "border-slate-200 bg-slate-50 opacity-60"}`}
                  data-testid={`u360-card-${k}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-bold text-slate-900">{t(`u360_sec_${k}`)}</span>
                    <button onClick={() => setInclude((p) => ({ ...p, [k]: !p[k] }))} data-testid={`u360-toggle-${k}`} aria-label={t(`u360_sec_${k}`)}
                      className={`relative h-7 w-12 rounded-full transition-colors ${include[k] ? "bg-brand" : "bg-slate-300"}`}>
                      <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${include[k] ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                  </div>
                  {s && (
                    <div className="mt-1.5 text-xs text-slate-600 whitespace-pre-wrap" data-testid={`u360-card-${k}-body`}>
                      <SectionBody k={k} s={s} t={t} />
                    </div>
                  )}
                  {k === "timecards" && s?.needs_crew_pick && s?.anon_hours != null && !(s?.entries || []).length && include[k] && (
                    <div className="mt-2 space-y-2 rounded-lg border border-brand/30 bg-brand/5 p-2.5" data-testid="u360-crew-pick">
                      {teamLoading && <p className="text-[11px] font-semibold text-slate-500 animate-pulse" data-testid="u360-crew-loading">{t("u360_picker_loading")}</p>}
                      <p className="text-[11px] font-bold text-slate-700">
                        {t("u360_pick_crew", { count: s.anon_count, hours: s.anon_hours })}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {team.map((m) => {
                          const sel = pickedCrew[m.id] != null;
                          return (
                            <button key={m.id} type="button" data-testid={`u360-crew-${m.id}`}
                              onClick={() => setPickedCrew((p) => { const n = { ...p }; if (sel) { delete n[m.id]; } else { n[m.id] = s.anon_hours; } return n; })}
                              className={`min-h-[40px] rounded-full border px-3.5 text-xs font-bold active:scale-95 ${sel ? "border-brand bg-brand text-white" : "border-slate-300 bg-white text-slate-700"}`}>
                              {m.name}
                            </button>
                          );
                        })}
                      </div>
                      {Object.keys(pickedCrew).map((id) => {
                        const m = team.find((x) => x.id === id);
                        return (
                          <div key={id} className="flex items-center gap-2" data-testid={`u360-crew-row-${id}`}>
                            <span className="flex-1 truncate text-xs font-semibold text-slate-700">{m?.name || id}</span>
                            <input type="number" min="0" max="24" step="0.5" value={pickedCrew[id]}
                              onChange={(e) => setPickedCrew((p) => ({ ...p, [id]: Math.max(0, Math.min(24, parseFloat(e.target.value) || 0)) }))}
                              data-testid={`u360-hours-${id}`}
                              className="w-20 rounded-lg border border-slate-300 px-2.5 py-1.5 text-center text-xs font-bold text-slate-900" />
                            <span className="text-[11px] text-slate-500">{t("u360_hours_short")}</span>
                          </div>
                        );
                      })}
                      <p className="text-[11px] text-slate-500">{t("u360_pick_crew_hint")}</p>
                      {extraWorkers.map((w, i) => (
                        <div key={`xw-${i}`} className="flex items-center gap-2" data-testid={`u360-xworker-row-${i}`}>
                          <input value={w.name} placeholder={t("u360_worker_name_ph")}
                            onChange={(e) => setExtraWorkers((p) => p.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x))}
                            data-testid={`u360-xworker-name-${i}`}
                            className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-900" />
                          <input type="number" min="0" max="24" step="0.5" value={w.hours}
                            onChange={(e) => setExtraWorkers((p) => p.map((x, xi) => xi === i ? { ...x, hours: Math.max(0, Math.min(24, parseFloat(e.target.value) || 0)) } : x))}
                            data-testid={`u360-xworker-hours-${i}`}
                            className="w-20 rounded-lg border border-slate-300 px-2.5 py-1.5 text-center text-xs font-bold text-slate-900" />
                          <span className="text-[11px] text-slate-500">{t("u360_hours_short")}</span>
                        </div>
                      ))}
                      <button type="button" onClick={() => setExtraWorkers((p) => [...p, { name: "", hours: s.anon_hours }])}
                        data-testid="u360-add-worker"
                        className="min-h-[36px] rounded-full border border-dashed border-brand/50 px-3 text-[11px] font-bold text-brand active:scale-95">
                        + {t("u360_add_worker")}
                      </button>
                    </div>
                  )}
                  {k === "timecards" && include[k] && (s?.entries || []).some((e) => e.user_id == null) && (
                    <div className="mt-2 space-y-1.5 rounded-lg border border-brand/30 bg-brand/5 p-2.5" data-testid="u360-worker-pick">
                      <p className="text-[11px] font-bold text-slate-700">{t("u360_pick_worker")}</p>
                      {(s?.entries || []).map((e, i) => e.user_id == null && (
                        <div key={i} className="flex items-center gap-2">
                          <span className="flex-1 truncate text-xs font-semibold text-slate-700">{e.name} · {e.hours}{t("u360_hours_short")}</span>
                          <select value={timecardPicks[i]?.user_id || ""}
                            onChange={(ev) => { const m = team.find((x) => x.id === ev.target.value);
                              setTimecardPicks((p) => { const n = { ...p }; if (m) n[i] = { user_id: m.id, name: m.name }; else delete n[i]; return n; }); }}
                            data-testid={`u360-workerpick-${i}`}
                            className="min-h-[40px] rounded-lg border border-slate-300 bg-white px-2 text-xs font-bold text-slate-900">
                            <option value="">{t("u360_pick_worker_ph")}</option>
                            {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                  {k === "equipment" && include[k] && (s?.items || []).some((it) => !it.equipment_id) && (
                    <div className="mt-2 space-y-1.5 rounded-lg border border-brand/30 bg-brand/5 p-2.5" data-testid="u360-equipment-pick">
                      {(s?.items || []).map((it, i) => !it.equipment_id && (
                        <div key={i} className="flex items-center gap-2">
                          <span className="flex-1 truncate text-xs font-semibold text-slate-700">{it.equipment_hint}{it.hours != null ? ` · ${it.hours}${t("u360_hours_short")}` : ""}</span>
                          <select value={equipPicks[i]?.equipment_id || ""}
                            onChange={(e) => { const opt = (it.choices || eqOptions).find((x) => x.id === e.target.value);
                              setEquipPicks((p) => { const n = { ...p }; if (opt) n[i] = { equipment_id: opt.id, equipment_name: opt.name, equipment_kind: opt.kind }; else delete n[i]; return n; }); }}
                            data-testid={`u360-eqpick-${i}`}
                            className="min-h-[40px] rounded-lg border border-slate-300 bg-white px-2 text-xs font-bold text-slate-900">
                            <option value="">{t("u360_pick_equipment")}</option>
                            {(it.choices || eqOptions).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                  {k === "receipt" && include[k] && receiptEdits && (
                    <div className="mt-2 grid grid-cols-3 gap-2" data-testid="u360-receipt-edit">
                      <input value={receiptEdits.vendor} onChange={(e) => setReceiptEdits((p) => ({ ...p, vendor: e.target.value }))} placeholder={t("jc_vendor")} data-testid="u360-rc-vendor" className="min-h-[40px] rounded-lg border border-slate-300 px-2.5 text-xs font-semibold text-slate-900" />
                      <input inputMode="decimal" value={receiptEdits.total} onChange={(e) => setReceiptEdits((p) => ({ ...p, total: e.target.value }))} placeholder={t("jc_total")} data-testid="u360-rc-total" className="min-h-[40px] rounded-lg border border-slate-300 px-2.5 text-xs font-semibold text-slate-900" />
                      <input type="date" value={receiptEdits.receipt_date} onChange={(e) => setReceiptEdits((p) => ({ ...p, receipt_date: e.target.value }))} data-testid="u360-rc-date" className="min-h-[40px] rounded-lg border border-slate-300 px-2.5 text-xs font-semibold text-slate-900" />
                    </div>
                  )}
                  <p className="mt-1 text-[11px] text-slate-400">{t(`u360_expl_${k}`)}</p>
                  {warn && (
                    <p className="mt-1 flex items-center gap-1 text-[11px] font-bold text-amber-700" data-testid={`u360-warn-${k}`}>
                      <AlertTriangle className="h-3 w-3" /> {t("u360_missing_info")}
                    </p>
                  )}
                  {(k === "daily_log" || k === "customer_message") && include[k] && (
                    <button onClick={() => { setEditKey(k); setEditText(s?.text || ""); }} data-testid={`u360-edit-${k}`}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-brand">
                      <Pencil className="h-3 w-3" /> {t("action_edit", "Edit")}
                    </button>
                  )}
                  {editKey === k && (
                    <div className="mt-2 space-y-2">
                      <textarea rows={3} value={editText} onChange={(e) => setEditText(e.target.value)}
                        data-testid={`u360-edit-text-${k}`}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs text-slate-900" />
                      <button onClick={() => setEditKey(null)} data-testid={`u360-edit-done-${k}`}
                        className="rounded-full bg-slateink px-4 py-1.5 text-[11px] font-bold text-white">{t("action_done", "Done")}</button>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="space-y-2 pt-1">
              <button onClick={approveAll} disabled={busy || !draft.project_id || !Object.values(include).some(Boolean)}
                data-testid="u360-approve"
                className="w-full inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-green-600 text-sm font-bold text-white active:scale-95 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
                {t("u360_approve_all")}
              </button>
              <div className="flex gap-2">
                <button onClick={() => setPhase("transcript")} data-testid="u360-edit-transcript"
                  className="flex-1 min-h-[44px] rounded-full border border-slate-300 text-xs font-bold text-slate-600 active:scale-95">
                  {t("u360_edit_transcript_btn")}
                </button>
                <button onClick={onBack} data-testid="u360-review-cancel"
                  className="flex-1 min-h-[44px] rounded-full border border-slate-300 text-xs font-bold text-slate-600 active:scale-95">
                  {t("action_cancel")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ---------- done ---------- */}
        {phase === "done" && result && (
          <div className="space-y-4" data-testid="u360-done">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-6 w-6" />
              <h3 className="font-heading text-lg font-extrabold">{t("u360_done_title")}</h3>
            </div>
            <p className="text-xs font-bold text-slate-500" data-testid="u360-saved-summary">
              {(() => {
                // Round 10 #6: per-kind saved summary — "Saved: log, 2 timecards, ..."
                const res = result.results || [];
                const counts = {};
                res.filter((r) => !r.skipped && !r.failed).forEach((r) => { counts[r.type] = (counts[r.type] || 0) + 1; });
                const parts = Object.entries(counts).map(([ty, n]) => t(`u360_sum_${ty}`, { count: n, defaultValue: `${n} ${ty.replace(/_/g, " ")}${n > 1 ? "s" : ""}` }));
                return parts.length ? `${t("u360_saved_prefix")}: ${parts.join(", ")}` : t("u360_saved_summary", { saved: 0, total: res.length });
              })()}
            </p>
            <div className="space-y-1.5">
              {(result.results || []).map((r, i) => (
                <button key={i} onClick={() => navigate(r.path)} data-testid={`u360-result-${i}`}
                  className={`flex w-full items-center justify-between rounded-xl border px-3.5 py-2.5 text-left min-h-[44px] active:scale-[0.99] ${r.failed ? "border-red-300 bg-red-50" : r.skipped ? "border-amber-300 bg-amber-50" : "border-green-200 bg-green-50"}`}>
                  <span>
                    <span className="block text-sm font-bold text-slate-900">{r.label}{r.skipped ? ` · ${t("u360_skipped")}` : ""}</span>
                    <span className="block text-xs text-slate-500">{r.failed ? (r.perm ? t("u360_perm_label") : t("u360_failed_label")) : r.detail}</span>
                  </span>
                  {r.failed ? <AlertTriangle className="h-4 w-4 text-red-600" /> : r.skipped ? <AlertTriangle className="h-4 w-4 text-amber-600" /> : <ArrowRight className="h-4 w-4 text-green-600" />}
                </button>
              ))}
            </div>
            {draft?.project_id && (
              <button onClick={() => setCuOpen(true)} data-testid="u360-send-customer"
                className="w-full inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-brand bg-white text-sm font-bold text-branddeep active:scale-95">
                {cuPrev?.customer?.name
                  ? t("cu_send_cta", { name: cuPrev.customer.name, count: (cuPrev.photo_ids || []).length })
                  : t("cu_send_cta_noname")}
              </button>
            )}
            <CustomerUpdateSheet open={cuOpen} onClose={() => setCuOpen(false)} projectId={draft?.project_id} />
            {result.failed?.length > 0 && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 space-y-2" data-testid="u360-partial">
                <p className="text-xs font-bold text-red-700">{t("u360_failed_title")}</p>
                <button onClick={retryFailed} disabled={busy} data-testid="u360-retry-failed"
                  className="w-full inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-red-600 text-sm font-bold text-white active:scale-95 disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {t("u360_retry_failed")} ({result.failed.map((k) => t(`u360_sec_${k}`)).join(", ")})
                </button>
              </div>
            )}
            {undoLeft > 0 && (
              <button onClick={undo} disabled={busy} data-testid="u360-undo"
                className="w-full inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border-2 border-red-300 text-sm font-bold text-red-600 active:scale-95 disabled:opacity-50">
                <Undo2 className="h-4 w-4" /> {t("u360_undo")} ({Math.floor(undoLeft / 60)}:{String(undoLeft % 60).padStart(2, "0")})
              </button>
            )}
            <button onClick={onBack} data-testid="u360-close"
              className="w-full min-h-[48px] rounded-full bg-brand text-sm font-bold text-white active:scale-95">
              {t("action_done", "Done")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
